import { useState } from "react";
import { Pause, Play, X, CalendarClock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatAdminError } from "@/lib/adminApi";
import { GhostButton, PrimaryButton, shortDate } from "@/components/admin/uiKit";

const PAUSEABLE_PLANS = new Set(["monthly", "biweekly"]);

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtFriendly(isoDate) {
  if (!isoDate) return null;
  try {
    const d = new Date(`${isoDate}T00:00:00Z`);
    return d.toLocaleDateString("en-US", {
      weekday: "long", month: "long", day: "numeric", timeZone: "UTC",
    });
  } catch {
    return isoDate;
  }
}

/**
 * Pause/Resume controls + status banner for an admin customer drawer.
 * Hidden when the customer is not on a pauseable plan (onetime / manual_*).
 */
export default function PauseServicePanel({ customer, nextJob, onChanged }) {
  const [pauseOpen, setPauseOpen] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);

  if (!customer) return null;
  const status = customer.subscription_status;
  const plan = customer.plan_code;

  // No banner for non-pauseable contexts. Manual flows have their own panels.
  if (status === "paused") {
    return (
      <>
        <div
          className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 sm:p-5"
          data-testid="pause-banner-paused"
        >
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-amber-100 text-amber-700 grid place-items-center shrink-0">
              <CalendarClock size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-amber-800 font-bold">
                Service paused
              </p>
              <p className="text-sm text-slate-800 mt-0.5">
                <strong>{fmtFriendly(customer.pause_resumes_on) || customer.pause_resumes_on}</strong> — auto-resumes on that date.
              </p>
              {customer.paused_at && (
                <p className="text-xs text-amber-700/80 mt-0.5">Paused {shortDate(customer.paused_at)}</p>
              )}
            </div>
          </div>
          <div className="mt-3 flex flex-col sm:flex-row sm:justify-end gap-2">
            <button
              type="button"
              onClick={() => setResumeOpen(true)}
              className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600"
              data-testid="pause-resume-button"
            >
              <Play size={14} /> Resume Now
            </button>
          </div>
        </div>
        {resumeOpen && (
          <ResumeConfirmDialog
            customer={customer}
            onClose={() => setResumeOpen(false)}
            onResumed={onChanged}
          />
        )}
      </>
    );
  }

  if (status === "active" && PAUSEABLE_PLANS.has(plan)) {
    const nextLabel = nextJob?.scheduled_for ? fmtFriendly(nextJob.scheduled_for) : null;
    return (
      <>
        <div
          className="rounded-xl border border-sky-200 bg-sky-50/70 p-4 sm:p-5"
          data-testid="pause-banner-active"
        >
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-sky-100 text-sky-700 grid place-items-center shrink-0">
              <CheckCircle2 size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-sky-800 font-bold">
                Active
              </p>
              <p className="text-sm text-slate-800 mt-0.5">
                {nextLabel ? <>Next cleaning <strong>{nextLabel}</strong>.</> : "No upcoming cleaning on the schedule."}
              </p>
            </div>
          </div>
          <div className="mt-3 flex flex-col sm:flex-row sm:justify-end gap-2">
            <button
              type="button"
              onClick={() => setPauseOpen(true)}
              className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg border border-amber-300 bg-white text-sm font-bold text-amber-800 hover:bg-amber-50"
              data-testid="pause-pause-button"
            >
              <Pause size={14} /> Pause Service
            </button>
          </div>
        </div>
        {pauseOpen && (
          <PauseServiceDialog
            customer={customer}
            onClose={() => setPauseOpen(false)}
            onPaused={onChanged}
          />
        )}
      </>
    );
  }

  return null;
}

function PauseServiceDialog({ customer, onClose, onPaused }) {
  const [resumeOn, setResumeOn] = useState(todayPlus(30));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await adminApi.post(`/admin/customers/${customer.id}/pause`, { resume_on: resumeOn });
      toast.success(`Service paused until ${fmtFriendly(resumeOn) || resumeOn}.`);
      onPaused?.();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.message) {
        setError(detail.message);
      } else {
        setError(formatAdminError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} testId="pause-dialog">
      <form
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        className="p-6 sm:p-7 space-y-5"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">Pause subscription</p>
            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "Outfit, ui-sans-serif" }}>
              Pause Service for {customer.full_name}
            </h2>
          </div>
          <CloseButton onClick={onClose} testId="pause-dialog-close" />
        </div>

        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-1">Return date</span>
          <input
            type="date"
            min={todayPlus(1)}
            value={resumeOn}
            onChange={(e) => setResumeOn(e.target.value)}
            className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
            data-testid="pause-dialog-resume-on"
            required
          />
        </label>

        <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-xs text-slate-700 leading-relaxed">
          Billing will pause via Stripe (<strong>no charges</strong>). Scheduled cleanings in this window will be paused — history preserved. Service resumes automatically on the return date, or you can resume early.
        </div>

        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="pause-dialog-error">
            {error}
          </div>
        )}

        <div className="pt-1 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <GhostButton onClick={onClose} type="button">Cancel</GhostButton>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-amber-500 text-white text-sm font-bold hover:bg-amber-600 disabled:opacity-60 w-full sm:w-auto"
            data-testid="pause-dialog-confirm"
          >
            <Pause size={14} /> {submitting ? "Pausing…" : "Confirm Pause"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ResumeConfirmDialog({ customer, onClose, onResumed }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await adminApi.post(`/admin/customers/${customer.id}/resume`);
      toast.success("Service resumed.");
      onResumed?.();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail?.message || formatAdminError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} testId="resume-dialog">
      <div className="p-6 sm:p-7 space-y-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">Resume subscription</p>
            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "Outfit, ui-sans-serif" }}>
              Resume {customer.full_name} now?
            </h2>
          </div>
          <CloseButton onClick={onClose} testId="resume-dialog-close" />
        </div>

        <p className="text-sm text-slate-700">
          Billing and cleanings will restart immediately. Stripe&rsquo;s pause is cleared and your subscription returns to its normal cycle.
        </p>

        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="resume-dialog-error">
            {error}
          </div>
        )}

        <div className="pt-1 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <GhostButton onClick={onClose} type="button">Cancel</GhostButton>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 disabled:opacity-60 w-full sm:w-auto"
            data-testid="resume-dialog-confirm"
          >
            <Play size={14} /> {submitting ? "Resuming…" : "Yes, Resume Now"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Modal({ children, onClose, testId }) {
  return (
    <div
      className="fixed inset-0 z-[60]"
      role="dialog"
      aria-modal="true"
      data-testid={testId}
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm"
      />
      <div className="absolute inset-0 flex items-start sm:items-center justify-center p-0 sm:p-6 overflow-y-auto">
        <div className="relative bg-white w-full sm:max-w-md sm:rounded-2xl shadow-2xl min-h-screen sm:min-h-0">
          {children}
        </div>
      </div>
    </div>
  );
}

function CloseButton({ onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 grid place-items-center shrink-0"
      data-testid={testId}
    >
      <X size={16} />
    </button>
  );
}
